import { useEffect, useState, useRef } from "react";
import { InputGroup } from "react-bootstrap";
import Select from "react-select";

import ToolTip from "components/ToolTip/ToolTip";
import { GridView } from "components/GridView/GridView";
import IconButton from "components/IconButton/IconButton";
import useExceptionHandler from "hooks/useExceptionHandler";
import { AutoComplete, Simplebar, SvgComponent } from "components";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import {
  buttonStyle,
  controlsProportion,
  initialWhere,
  initialWhereModal,
} from "..";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import { ClienteManager } from "managers";
import { Cliente } from "models/ECF/Cliente";
import "../Lookup.style.scss";

export const ClienteLookup = ({
  initialValue = { label: "", value: "" },
  onChange,
  isDisabled,
  isInput = false,
}) => {
  const ref = useRef(null);
  const [showCanvas, setShowCanvas] = useState(false);
  const [clienteListModal, setClienteListModal] = useState([]);
  const [whereModal, setWhereModal] = useState({
    ...initialWhereModal,
    Estatus: "",
  });
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [selectedValue, setSelectedValue] = useState({
    label: "",
    value: "",
    Rnc: "",
    RazonSocial: "",
    Estatus: "",
  });

  const { handleException } = useExceptionHandler();
  const { setPaginationValuesWhere } = usePaginationValuesWhere();

  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    { value: "ACTIVO", label: "ACTIVO" },
    { value: "SUSPENDIDO", label: "SUSPENDIDO" },
  ];

  const LabelsHolders = {
    placeholder: "Buscar RNC",
    message: "Seleccionar RNC",
  };
  
  useEffect(() => {
    if (whereModal != initialWhereModal) {
      buscar();
    }
  }, [whereModal]);
  
  useEffect(() => {
    if (selectedValue == undefined) return;
    if (selectedValue.value !== null && selectedValue.value !== "") {
      onChange(selectedValue);
    } else if (selectedValue.value === "" && selectedValue.Rnc === "") {
      onChange({ value: "", label: "", Rnc: "", RazonSocial: "", Estatus: "" });
    }
  }, [selectedValue]);

  useEffect(() => {
    if (
      initialValue.value &&
      initialValue.value !== "" &&
      initialValue.value !== selectedValue.value
    ) {
      setSelectedValue({
        label: initialValue.label || initialValue.value,
        value: initialValue.value,
        Rnc: initialValue.Rnc || initialValue.value,
        RazonSocial: initialValue.RazonSocial || "",
        Estatus: initialValue.Estatus || "",
      });
    }
  
    if (initialValue.value === -1) {
      setSelectedValue({
        label: "",
        value: "",
        Rnc: "",
        RazonSocial: "",
        Estatus: "",
      });
    }
  }, [initialValue]);


  function buscar() {
    const whereWithPaginationValues = {
      ...whereModal,
      ...pagination,
    };
    ClienteManager.listarRNCs(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        setClienteListModal(res.Data);
      })
      .catch((err) => handleException(err));
  }

  const handleCerrar = () => {
    setShowCanvas(false);
    setWhereModal({ ...initialWhereModal, Estatus: "" });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhereModal({ ...whereModal });
  };

  const getSuggestions = async (value) => {
    const inputValue = value.trim().toLowerCase();
    const res = await ClienteManager.listarRNCs({
      ...initialWhere,
      Documento: inputValue,
      Estatus: "",
    });
    return res.Data.map(item => ({
      label: item.Rnc,
      value: item.Rnc,
      Rnc: item.Rnc,
      RazonSocial: item.RazonSocial,
      Estatus: item.Estatus
    }));
  };

  return (
    <>
      <InputGroup className="w-100">
        <div
          style={{
            width: controlsProportion.input,
          }}
        >
          <AutoComplete
            placeholder={LabelsHolders.placeholder}
            getList={getSuggestions}
            parentValue={selectedValue}
            setParentValue={(val) => {
              if (!val) return;

              // Selección desde la lista: value definido y no vacío
              if (val.value !== null && val.value !== undefined && val.value !== "") {
                setSelectedValue({
                  value: val.value,
                  label: val.label ?? val.value,
                  Rnc: val.Rnc ?? val.value,
                  RazonSocial: val.RazonSocial ?? "",
                  Estatus: val.Estatus ?? "",
                });
              } else if (val.label !== undefined && val.label !== null) {
                // Escritura manual sin seleccionar de lista — usar label como value
                const typed = val.label.trim();
                if (typed !== "") {
                  setSelectedValue({
                    value: typed,
                    label: typed,
                    Rnc: typed,
                    RazonSocial: "",
                    Estatus: "",
                  });
                } else {
                  // Campo borrado — limpiar
                  setSelectedValue({ value: "", label: "", Rnc: "", RazonSocial: "", Estatus: "" });
                }
              }
            }}
          />
        </div>
        <InputGroup.Append style={{ width: controlsProportion.button }}>
          <AnimatedIconButton
            size="40px"
            height="18"
            width="18"
            message={LabelsHolders.message}
            style={buttonStyle}
            disabled={isDisabled}
            iconName="search"
            onClick={() => {
              setShowCanvas(true);
              setTimeout(() => {
                buscar();
              }, 200);
            }}
          />
        </InputGroup.Append>
      </InputGroup>
      {showCanvas && (
        <Simplebar
          ref={ref}
          backdrop={true}
          placement={"end"}
          portal={false}
          size="lg"
          useLoading={false}
          visible={showCanvas}
          setShow={(e) => {
            setShowCanvas(e);
          }}
          title={LabelsHolders.message}
          btnLeft={{
            show: false,
            func: () => {},
          }}
          btnRight={{
            show: false,
            func: () => {},
          }}
        >
          <GridView
            pagination={pagination}
            setPagination={setPagination}
            allowPaging={pagination.EsPaginable}
            pageSize={pagination.CantidadPorPagina}
            pageIndex={pagination.IndiceDePagina}
            dataSource={clienteListModal}
            onPageIndexChanging={onPageIndexChanging}
            where={whereModal}
            setWhere={setWhereModal}
          >
            <GridView.Column
              bodyRenderer={(row) => {
                return (
                  <div className="float-right">
                    <IconButton
                      onClick={() => {
                        setSelectedValue({
                          value: row.Rnc,
                          label: row.Rnc,
                          Rnc: row.Rnc,
                          RazonSocial: row.RazonSocial,
                          Estatus: row.Estatus,
                        });
                        handleCerrar();
                      }}
                    >
                      <ToolTip message="Seleccionar">
                        <div>
                          <SvgComponent
                            height="20"
                            width="20"
                            iconName="check"
                          />
                        </div>
                      </ToolTip>
                    </IconButton>
                  </div>
                );
              }}
            />
            <GridView.Column
              headerText="RNC"
              dataField="Rnc"
              sortExpression="Rnc"
            />
            <GridView.Column
              headerText="Nombre"
              dataField="RazonSocial"
              sortExpression="RazonSocial"
            />
             <GridView.Column
              headerText="Estatus"
              dataField="Estatus"
              sortExpression="Estatus"
              filterRenderer={() => {
                return (
                  <Select
                    className="react-select primary"
                    options={ESTATUS_LIST}
                    value={ESTATUS_LIST.filter(function (option) {
                      return (
                        option.value ===
                        (whereModal.Estatus
                          ? whereModal.Estatus
                          : ESTATUS_LIST[0].value)
                      );
                    })}
                    name="Estatus"
                    onChange={(e) =>
                      setWhereModal({
                        ...whereModal,
                        Estatus: e.value,
                      })
                    }
                  />
                );
              }}
            />
          </GridView>
        </Simplebar>
      )}
    </>
  );
};